/**
 * Delad CSV/Excel-export för tabellkolumner (semicolon-delimiter för svensk Excel).
 */

import type { DataTableColumn } from '@/components/common/DataTable';

export const CSV_DELIMITER = ';';

function getExportCellValue<T extends object>(
  col: DataTableColumn<T>,
  row: T,
  forExcel: boolean
): string {
  const val = row[col.key as keyof T];
  let str = col.csvValue ? col.csvValue(val, row) : val != null ? String(val) : '';
  if (forExcel && str.startsWith('="') && str.endsWith('"')) {
    str = str.slice(2, -1).replace(/""/g, '"');
  }
  return str;
}

export function rowToExportCells<T extends object>(
  row: T,
  columns: DataTableColumn<T>[],
  forExcel = false
): string[] {
  return columns.map((col) => getExportCellValue(col, row, forExcel));
}

export function exportTableToCsv<T extends object>(
  columns: DataTableColumn<T>[],
  data: T[],
  filename: string
): void {
  const headerRow = columns.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(CSV_DELIMITER);
  const rows = data.map((row) =>
    rowToExportCells(row, columns, false)
      .map((cell) => `"${cell.replace(/"/g, '""')}"`)
      .join(CSV_DELIMITER)
  );
  const csv = [headerRow, ...rows].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function exportTableToExcel<T extends object>(
  columns: DataTableColumn<T>[],
  data: T[],
  filename: string,
  sheetName = 'Export'
): Promise<void> {
  const XLSX = await import('xlsx');
  const wsData = [
    columns.map((c) => c.label),
    ...data.map((row) => rowToExportCells(row, columns, true)),
  ];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename);
}
